import React, { useState, useEffect, useContext, useRef } from 'react';
import { Card, Spinner } from 'react-bootstrap';
import { GoGraph, GoFileZip, GoFileDirectory } from 'react-icons/go';
import { BsSearch, BsSortAlphaDown, BsSortNumericDown, BsChevronDown, BsChevronRight } from 'react-icons/bs';
import AppContext from 'Utils/AppContext';
import { filterTablesForDisplay } from 'Utils/entityLevels';
import { commafy, uiLabel } from 'Utils';
import { isArchivePath } from 'Utils/archivePath';
import { FONT_MONO, STATUS } from 'Utils/styleTokens';
import FileTypeChip from 'DataSources/file/FileTypeChip';
import EntityMarkSeries from '../EntityPanel/EntityMarkSeries';
import {
  getFolderChunk,
  buildEntityTablesFromOverview,
  buildTableSampledRowCountsMapForEntityTables,
} from 'DataDiscovery/discoverApi';

// Sampler types a Folder Search run can stamp on its rows (discovery_run_table.meta
// .sampler_type, surfaced as sampler_type on each chunk item). Drives the file-type
// filter chips. Legacy rows (pre-2026-07-15 runs) carry no sampler_type; those runs
// were document-only, so missing means FILE_DOCUMENT_FOLDER.
// Entity types that always render danger-tinted (style guide: high-sensitivity
// classifications read as risk, not neutral metadata).
const HIGH_SENSITIVITY_ENTITIES = new Set(['CREDIT_CARD', 'SSN']);

const SAMPLER_TYPE_OPTIONS = [
  'FILE_DOCUMENT_FOLDER',
  'FILE_DELIMITED_FOLDER',
  'FILE_FIXED_WIDTH_FOLDER',
  'FILE_JSON_FOLDER',
  'FILE_XML_FOLDER',
  'FILE_PARQUET_FOLDER',
  'FILE_EXCEL_FOLDER',
  'FILE_HL7_FOLDER',
  'FILE_EDI_FOLDER',
];

// A presence row (key material: .pfx/.kdbx/.jks/.pem) has no sampler_type because nothing
// was sampled — the file's EXISTENCE is the finding. Defaulting it to FILE_DOCUMENT_FOLDER
// made a .pfx render as a Document and vanish behind the Document type filter, which is the
// worst place to hide the finding a credential hunt is looking for.
export function isPresenceRow(table) {
  return Boolean(table && table.presence_finding_type);
}

function rowSamplerType(table) {
  if (isPresenceRow(table)) return 'KEY_MATERIAL';
  return table.sampler_type || 'FILE_DOCUMENT_FOLDER';
}

function displayFolderLabel(folderPath) {
  if (!folderPath) return '(scan root)';
  return folderPath;
}

// Human word for a folder's uniform delimiter (structure_delimiter). Comma is the
// unmarked default and renders nothing; unknown delimiters render quoted.
function delimiterLabel(d) {
  if (!d || d === ',') return null;
  if (d === '\t') return 'tab-delimited';
  if (d === '|') return 'pipe-delimited';
  if (d === ';') return 'semicolon-delimited';
  return `“${d}”-delimited`;
}

// Top-N entities of a folder's rollup, largest first, for the collapsed heading.
function topEntities(rollup, n = 3) {
  const entries = Object.entries(rollup || {});
  entries.sort((a, b) => b[1] - a[1]);
  return { top: entries.slice(0, n), more: Math.max(0, entries.length - n) };
}

/**
 * Folder-grouped results for Folder Search (FILE_GENERIC_FOLDER) runs. Renders one
 * collapsible section per folder from the run's folder INDEX (small, always loaded);
 * each folder's per-file rows are lazy-fetched as a CHUNK on first expand — the old
 * monolithic view rendered every file in one unbounded scatter (40px per row).
 * Chunks tile the run, so the union of all expanded folders equals the old view.
 *
 * `entity` scopes the scatter marks (and pre-filters folders to those whose rollup
 * contains the entity) so EntityPanel can reuse this component for generic runs.
 */
const FolderResultsPanel = (props) => {
  const { entityLevels } = useContext(AppContext);
  const searchid = props.searchid;
  const dataLabel = props.dataLabel || 'files';
  const entity = props.entity || 'ALL';
  const folders = props.folders || [];
  const partialTick = props.partialTick;

  const [expanded, setExpanded] = useState({});   // folder_path -> true
  const [chunks, setChunks] = useState({});       // folder_path -> {loading, error, tables}
  const [searchField, setSearchField] = useState('');
  // Folder ordering: 'findings' (count), 'name' (path), default findings desc.
  const [sortBy, setSortBy] = useState('findings');
  const [sortDir, setSortDir] = useState('desc');
  const [typeFilter, setTypeFilter] = useState(null); // null = all, else Set of sampler types
  // Incremental folder-list rendering: big runs can index hundreds of folders; render
  // in slices so Expand all never turns into a render (or fetch) stampede.
  const PAGE_STEP = 25;
  const [visibleLimit, setVisibleLimit] = useState(PAGE_STEP);

  // "Expand all" fetches chunks in small awaited batches (see expandAll) so a
  // 25-folder page streams in waves instead of firing every request at once.
  const MAX_CONCURRENT_CHUNK_LOADS = 4;
  const inFlight = useRef(new Set());
  const chunksRef = useRef(chunks);
  chunksRef.current = chunks;

  const doLoadChunk = async (folderPath) => {
    if (inFlight.current.has(folderPath)) return;
    inFlight.current.add(folderPath);
    setChunks((prev) => ({ ...prev, [folderPath]: { ...(prev[folderPath] || {}), loading: true, error: null } }));
    try {
      const chunk = await getFolderChunk(searchid, folderPath);
      setChunks((prev) => ({
        ...prev,
        [folderPath]: { loading: false, error: null, tables: chunk?.tables || [] },
      }));
    } catch (e) {
      console.log('folder chunk', folderPath, e);
      setChunks((prev) => ({
        ...prev,
        [folderPath]: { loading: false, error: e?.message || 'Failed to load folder results', tables: [] },
      }));
    } finally {
      inFlight.current.delete(folderPath);
    }
  };

  const loadChunk = (folderPath, force = false) => {
    const existing = chunksRef.current[folderPath];
    if (!force && existing && !existing.error && !existing.loading) return;
    doLoadChunk(folderPath);
  };

  const toggleFolder = (folderPath) => {
    setExpanded((prev) => {
      const next = { ...prev, [folderPath]: !prev[folderPath] };
      if (next[folderPath]) loadChunk(folderPath);
      return next;
    });
  };

  // Fresh partial snapshot landed (5-minute poll while RUNNING): refetch the chunks
  // the user has open so expanded folders track the run without a manual collapse
  // and re-expand. Collapsed folders stay lazy.
  const lastTick = useRef(partialTick);
  useEffect(() => {
    if (partialTick === lastTick.current) return;
    lastTick.current = partialTick;
    Object.keys(expanded).forEach((p) => {
      if (expanded[p]) loadChunk(p, true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partialTick]);

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir(key === 'findings' ? 'desc' : 'asc');
    }
  };

  const toggleType = (t) => {
    setTypeFilter((prev) => {
      const next = new Set(prev || []);
      if (next.has(t)) next.delete(t); else next.add(t);
      return next.size === 0 ? null : next;
    });
  };

  // Folder list: entity scope -> search -> sort.
  let visible = folders;
  if (entity && entity !== 'ALL') {
    visible = visible.filter((f) => (f.entity_rollup || {})[entity] > 0);
  }
  const term = searchField.trim().toLowerCase();
  if (term) {
    visible = visible.filter((f) => (f.folder_path || '(scan root)').toLowerCase().includes(term));
  }
  visible = visible.slice().sort((a, b) => {
    let cmp;
    if (sortBy === 'name') {
      cmp = String(a.folder_path).localeCompare(String(b.folder_path));
    } else {
      cmp = (a.finding_count || 0) - (b.finding_count || 0);
    }
    return sortDir === 'asc' ? cmp : -cmp;
  });
  // Only the rendered slice participates in Expand all — chunks stream through the
  // load pool, and deeper pages stay untouched until "Show more".
  const rendered = visible.slice(0, visibleLimit);
  const anyExpanded = rendered.some((f) => expanded[f.folder_path]);

  // Open the first folder once the index arrives so the page starts with results
  // visible; the rest stay collapsed (lazy). One-shot — collapsing it afterwards
  // is respected.
  const autoExpanded = useRef(false);
  useEffect(() => {
    if (autoExpanded.current || rendered.length === 0) return;
    autoExpanded.current = true;
    const first = rendered[0].folder_path;
    setExpanded((prev) => ({ ...prev, [first]: true }));
    loadChunk(first);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rendered.length]);

  const expandAll = async () => {
    setExpanded((prev) => {
      const next = { ...prev };
      rendered.forEach((f) => { next[f.folder_path] = true; });
      return next;
    });
    const paths = rendered
      .map((f) => f.folder_path)
      .filter((p) => {
        const c = chunksRef.current[p];
        return !(c && !c.error && !c.loading);
      });
    for (let i = 0; i < paths.length; i += MAX_CONCURRENT_CHUNK_LOADS) {
      // eslint-disable-next-line no-await-in-loop
      await Promise.all(paths.slice(i, i + MAX_CONCURRENT_CHUNK_LOADS).map((p) => doLoadChunk(p)));
    }
  };

  const collapseAll = () => setExpanded({});

  const sortBtnStyle = (active) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    cursor: 'pointer',
    border: '1px solid #E5E8ED',
    borderRadius: 8,
    background: active ? '#E9EEFF' : '#fff',
    color: active ? '#1E40AF' : '#5C6473',
    fontSize: 13,
    padding: '5px 10px',
  });
  const arrow = (key) => (sortBy === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '');

  const renderFolderBody = (folder) => {
    const state = chunks[folder.folder_path];
    if (!state || state.loading) {
      return (
        <div className='d-flex justify-content-center' style={{ padding: 16 }}>
          <Spinner variant='primary' animation='border' size='sm' />
        </div>
      );
    }
    if (state.error) {
      return (
        <div className='text-center' style={{ padding: 16, fontSize: 13, color: '#B91C1C' }}>
          {state.error}
        </div>
      );
    }
    let tables = filterTablesForDisplay(state.tables || [], entityLevels);
    if (typeFilter) {
      // Presence rows are exempt from the file-type filter. They are not "a file of type X"
      // — nothing was sampled, so no sampler type describes them — and filtering by any
      // document/table type would silently drop the key-material findings, which is exactly
      // what used to happen when they defaulted to FILE_DOCUMENT_FOLDER.
      tables = tables.filter((t) => isPresenceRow(t) || typeFilter.has(rowSamplerType(t)));
    }
    if (tables.length === 0) {
      return (
        <div className='text-center' style={{ padding: 16, fontSize: 13, color: '#8A92A0' }}>
          {typeFilter ? 'No files of the selected type in this folder.' : `No ${dataLabel} in this folder.`}
        </div>
      );
    }
    const entityTables = buildEntityTablesFromOverview(tables);
    const tablesToSampledRows = buildTableSampledRowCountsMapForEntityTables(tables);
    return (
      <EntityMarkSeries
        hideHeader
        allEntities
        defaultSamplerType='FILE_DOCUMENT_FOLDER'
        searchid={searchid}
        dataLabel={dataLabel}
        lowConfidence={false}
        overviewTables={tables}
        entityTables={entityTables}
        entity={entity}
        tablesToSampledRows={tablesToSampledRows}
      />
    );
  };

  return (
    <Card className='shadow-sm widget-card text-left'>
      <div className='widget-header d-flex align-items-center p-3' style={{ marginBottom: 0, flexWrap: 'wrap', gap: 12 }}>
        <div className='d-flex align-items-center' style={{ whiteSpace: 'nowrap' }}>
          <GoGraph className='mr-1 widget-header-icon widget-header-icon-lg' />
          Sensitive Data by Folder
        </div>
        <div className='d-flex align-items-center ml-auto' style={{ gap: 8, flexWrap: 'wrap' }}>
          <button
            type='button'
            style={sortBtnStyle(false)}
            onClick={anyExpanded ? collapseAll : expandAll}
            title={anyExpanded
              ? 'Collapse all folders'
              : 'Expand the folders shown below (contents load a few at a time)'}
          >
            {anyExpanded ? 'Collapse all' : 'Expand all'}
          </button>
          <button
            type='button'
            style={sortBtnStyle(sortBy === 'name')}
            onClick={() => handleSort('name')}
            title='Sort folders by path'
          >
            <BsSortAlphaDown size={14} />
            Name{arrow('name')}
          </button>
          <button
            type='button'
            style={sortBtnStyle(sortBy === 'findings')}
            onClick={() => handleSort('findings')}
            title='Sort folders by number of findings'
          >
            <BsSortNumericDown size={14} />
            Findings{arrow('findings')}
          </button>
          <div style={{ position: 'relative', minWidth: 200, maxWidth: 300 }}>
            <BsSearch size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#8A92A0' }} />
            <input
              value={searchField}
              onChange={(e) => { setSearchField(e.target.value); setVisibleLimit(PAGE_STEP); }}
              placeholder='Search folders…'
              style={{
                width: '100%', borderRadius: 8, border: '1px solid #E5E8ED', background: '#fff',
                padding: '6px 12px 6px 30px', fontSize: 14, outline: 'none',
              }}
            />
          </div>
        </div>
        <div className='w-100 d-flex align-items-center' style={{ gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: '#5C6473' }}>File type:</span>
          {SAMPLER_TYPE_OPTIONS.map((t) => (
            <span
              key={t}
              onClick={() => toggleType(t)}
              style={{
                cursor: 'pointer',
                borderRadius: 10,
                padding: 2,
                border: typeFilter && typeFilter.has(t) ? '2px solid #315DFF' : '2px solid transparent',
                opacity: !typeFilter || typeFilter.has(t) ? 1 : 0.45,
                display: 'inline-flex',
              }}
              title={typeFilter && typeFilter.has(t) ? 'Click to remove from filter' : 'Click to filter by this type'}
            >
              <FileTypeChip type={t} />
            </span>
          ))}
          {typeFilter && (
            <button
              type='button'
              onClick={() => setTypeFilter(null)}
              style={{ border: 'none', background: 'none', color: '#315DFF', fontSize: 12, cursor: 'pointer' }}
            >
              Clear filter
            </button>
          )}
        </div>
      </div>
      <Card.Body style={{ paddingTop: 8 }}>
        {visible.length === 0 ? (
          <div className='text-center' style={{ padding: 24, fontSize: 14, color: '#8A92A0' }}>
            {term ? `No folders match “${searchField.trim()}”.` : 'No folders with sampled files to display.'}
          </div>
        ) : rendered.map((folder) => {
          const isOpen = !!expanded[folder.folder_path];
          const { top, more } = topEntities(folder.entity_rollup);
          return (
            <div key={folder.folder_path || '(root)'} style={{ borderTop: '1px solid #EDEFF2' }}>
              <div
                onClick={() => toggleFolder(folder.folder_path)}
                data-testid='folder-results-heading'
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '10px 4px',
                  cursor: 'pointer', flexWrap: 'wrap',
                }}
              >
                {isOpen ? <BsChevronDown size={13} /> : <BsChevronRight size={13} />}
                {isArchivePath(folder.folder_path)
                  ? <GoFileZip aria-label='Inside archive' style={{ flexShrink: 0, opacity: 0.7 }} />
                  : <GoFileDirectory style={{ flexShrink: 0, opacity: 0.7 }} />}
                <span style={{ fontWeight: 600, fontFamily: 'monospace', fontSize: 13 }}>
                  {displayFolderLabel(folder.folder_path)}
                </span>
                {/* Detected structure used for discovery — absent for mixed/legacy folders,
                    and FileTypeChip self-suppresses on a falsy type. */}
                <FileTypeChip type={folder.structure_type} />
                <span style={{ fontSize: 12, color: '#5C6473' }}>
                  {commafy(folder.file_count)} {dataLabel} · {commafy(folder.finding_count)} findings
                  {delimiterLabel(folder.structure_delimiter) && (
                    <span style={{ fontFamily: FONT_MONO }}>
                      {' · '}{delimiterLabel(folder.structure_delimiter)}
                    </span>
                  )}
                </span>
                <span className='ml-auto d-inline-flex align-items-center' style={{ gap: 6, flexWrap: 'wrap' }}>
                  {top.map(([ent, count]) => (
                    <span
                      key={ent}
                      style={{
                        fontSize: 11, borderRadius: 6, fontFamily: FONT_MONO,
                        background: HIGH_SENSITIVITY_ENTITIES.has(ent) ? STATUS.danger.bg : STATUS.neutral.bg,
                        color: HIGH_SENSITIVITY_ENTITIES.has(ent) ? STATUS.danger.text : STATUS.neutral.text,
                        padding: '2px 8px', whiteSpace: 'nowrap',
                      }}
                    >
                      {uiLabel(ent)} {commafy(count)}
                    </span>
                  ))}
                  {more > 0 && (
                    <span style={{ fontSize: 11, color: '#5C6473' }}>+{more} more</span>
                  )}
                </span>
              </div>
              {isOpen && (
                <div style={{ paddingLeft: 20, paddingBottom: 8 }}>
                  {renderFolderBody(folder)}
                </div>
              )}
            </div>
          );
        })}
        {visible.length > rendered.length && (
          <div className='text-center' style={{ padding: '12px 0', borderTop: '1px solid #EDEFF2' }}>
            <button
              type='button'
              onClick={() => setVisibleLimit((n) => n + PAGE_STEP)}
              style={{
                border: '1px solid #E5E8ED', borderRadius: 8, background: '#fff',
                color: '#315DFF', fontSize: 13, padding: '6px 14px', cursor: 'pointer',
              }}
            >
              Show more folders ({visible.length - rendered.length} remaining)
            </button>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default FolderResultsPanel;
